import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users as UsersIcon, Minus, Plus } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Button } from './ui/button';

const GuestPicker = ({ guests, onGuestsChange, maxGuests = 16 }) => {
  const { t } = useTranslation();
  const [adults, setAdults] = useState(guests?.adults || 1);
  const [children, setChildren] = useState(guests?.children || 0);

  const handleUpdate = (type, value) => {
    if (type === 'adults') {
      const newAdults = Math.max(1, value);
      const totalGuests = newAdults + children;
      
      // Prevent exceeding max guests
      if (totalGuests > maxGuests) {
        return;
      }
      
      setAdults(newAdults);
      onGuestsChange({ adults: newAdults, children });
    } else {
      const newChildren = Math.max(0, value);
      const totalGuests = adults + newChildren;
      
      // Prevent exceeding max guests
      if (totalGuests > maxGuests) {
        return;
      }
      
      setChildren(newChildren);
      onGuestsChange({ adults, children: newChildren });
    }
  };

  const totalGuests = adults + children;
  const isMaxReached = totalGuests >= maxGuests;

  return (
    // See DatePicker.jsx for the full rationale — `modal={false}` lets the
    // popover coexist with a parent Sheet/Dialog's focus trap on iOS Safari.
    <Popover modal={false}>
      <PopoverTrigger asChild>
        <div data-testid="guest-picker-trigger" className="flex-1 px-6 py-3 cursor-pointer hover:bg-gray-50 rounded-full transition-colors">
          <div className="flex items-center gap-3">
            <UsersIcon className="w-5 h-5 text-gray-600" />
            <div>
              <label className="text-xs font-semibold text-gray-800 block mb-1">
                {t('search.guests')}
              </label>
              <p className="text-sm text-gray-900" data-testid="guest-picker-summary">
                {totalGuests} {totalGuests === 1 ? t('common.guest') : t('common.guests')}
              </p>
            </div>
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 z-[200]"
        align="end"
        data-testid="guest-picker-popover"
        sideOffset={6}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="space-y-4">
          {/* Max guests warning */}
          {isMaxReached && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-2">
              <p className="text-sm text-yellow-800">
                ⚠️ Maximum capacity: {maxGuests} {maxGuests === 1 ? 'guest' : 'guests'}
              </p>
            </div>
          )}

          {/* Adults */}
          <div className="flex items-center justify-between" data-testid="adults-row">
            <div>
              <p className="font-medium">{t('search.adults')}</p>
              <p className="text-sm text-gray-500" data-testid="adults-age-description">Ages 18+</p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 rounded-full"
                onClick={() => handleUpdate('adults', adults - 1)}
                disabled={adults <= 1}
                data-testid="adults-decrement"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="w-8 text-center" data-testid="adults-count">{adults}</span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 rounded-full"
                onClick={() => handleUpdate('adults', adults + 1)}
                disabled={isMaxReached}
                data-testid="adults-increment"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Children */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{t('search.children')}</p>
              <p className="text-sm text-gray-500">Ages 2-12</p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 rounded-full"
                onClick={() => handleUpdate('children', children - 1)}
                disabled={children <= 0}
                data-testid="children-decrement"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="w-8 text-center" data-testid="children-count">{children}</span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 rounded-full"
                onClick={() => handleUpdate('children', children + 1)}
                disabled={isMaxReached}
                data-testid="children-increment"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default GuestPicker;
